import { Modal } from '@mui/material';

import { Text } from '@/components/UI/Typography/Text';
import { convertLink } from '@/utils/convert';
import { openStorageDownload } from '@/utils/storageDownload';
import { formatPblDisplayDate } from '@/utils/pblDate';
import { translatePblDisplayList, translatePblDisplayValue } from '@/utils/pblDisplayTranslation';

import {
  CheckIcon,
  InfoLabel,
  InfoModalSurface,
  InfoValue,
  ModalCloseButton,
  ModalInfoGrid,
  ModalSection,
  SectionTitle,
  SignaturePreview,
  SignaturePreviewImage,
  SupportingDocumentLink,
} from './styles';
import type { PblOcManagedCase } from './types';
import { useTranslation } from 'react-i18next';

type CaseInformationModalItem = Pick<
  PblOcManagedCase,
  | 'enquirer'
  | 'enquiryCategory'
  | 'urgency'
  | 'courtDeadline'
  | 'factualBackground'
  | 'specificQueries'
  | 'primaryLegalServiceNeeds'
  | 'preferredConsultationMethods'
  | 'supportingDocuments'
  | 'enquirerSignatureDate'
  | 'enquirerSignatureImage'
>;

export const CaseInformationModal = ({
  item,
  open,
  onClose,
}: {
  item: CaseInformationModalItem;
  open: boolean;
  onClose: () => void;
}) => {
  //   @ts-ignore
  const { t } = useTranslation();
  return (
  <Modal open={open} onClose={onClose} aria-labelledby="oc-case-information-title">
    <InfoModalSurface>
      <ModalCloseButton type="button" aria-label={t('pbl.pagesHardcoded.ocManageCases.caseInformationModal.closeInformationModal')} onClick={onClose}>
        x
      </ModalCloseButton>

      <ModalSection>
        <SectionTitle id="oc-case-information-title">{t('pbl.pagesHardcoded.ocManageCases.caseInformationModal.enquirerSInformation')}</SectionTitle>
        <ModalInfoGrid>
          <InfoLabel>{t('pbl.pagesHardcoded.ocManageCases.caseInformationModal.fullNameEnglishChinese')}</InfoLabel>
          <InfoValue>{item.enquirer.fullName}</InfoValue>
          <InfoLabel>{t('pbl.pagesHardcoded.ocManageCases.caseInformationModal.hkidPassportNoPartialFirst4DigitsOnly')}</InfoLabel>
          <InfoValue>{item.enquirer.identityPartial}</InfoValue>
          <InfoLabel>{t('pbl.pagesHardcoded.ocManageCases.caseInformationModal.phoneNumber')}</InfoLabel>
          <InfoValue>{item.enquirer.phone}</InfoValue>
          <InfoLabel>{t('pbl.pagesHardcoded.ocManageCases.caseInformationModal.emailAddress')}</InfoLabel>
          <InfoValue>{item.enquirer.email}</InfoValue>
          <InfoLabel>{t('pbl.pagesHardcoded.ocManageCases.caseInformationModal.residentialAddress')}</InfoLabel>
          <InfoValue>{item.enquirer.residentialAddress}</InfoValue>
        </ModalInfoGrid>
      </ModalSection>

      <ModalSection>
        <SectionTitle>{t('pbl.pagesHardcoded.ocManageCases.caseInformationModal.enquiryCategory')}</SectionTitle>
        <ModalInfoGrid>
          <InfoLabel>{t('pbl.pagesHardcoded.ocManageCases.caseInformationModal.selectOnlyOneMostRelevantCategoryInRelation')}</InfoLabel>
          <InfoValue>{translatePblDisplayValue(t, item.enquiryCategory)}</InfoValue>
        </ModalInfoGrid>
      </ModalSection>

      {(item.urgency || item.courtDeadline) && (
        <ModalSection>
          <SectionTitle>{t('pbl.citizenAuthorizationCaseCreate.caseApplication.urgency.title')}</SectionTitle>
          <ModalInfoGrid>
            <InfoLabel>{t('pbl.citizenAuthorizationCaseCreate.caseApplication.urgency.level')}</InfoLabel>
            <InfoValue>{item.urgency ? t(`pbl.citizenAuthorizationCaseCreate.caseApplication.urgency.${item.urgency}`) : '-'}</InfoValue>
            <InfoLabel>{t('pbl.citizenAuthorizationCaseCreate.caseApplication.urgency.courtDeadline')}</InfoLabel>
            <InfoValue>{item.courtDeadline || '-'}</InfoValue>
          </ModalInfoGrid>
        </ModalSection>
      )}

      <ModalSection>
        <SectionTitle>{t('pbl.pagesHardcoded.ocManageCases.caseInformationModal.detailedDescriptionOfLegalIssue')}</SectionTitle>
        <ModalInfoGrid>
          <InfoLabel>{t('pbl.pagesHardcoded.ocManageCases.caseInformationModal.factualBackground')}</InfoLabel>
          <InfoValue>{item.factualBackground}</InfoValue>
          <InfoLabel>{t('pbl.pagesHardcoded.ocManageCases.caseInformationModal.specificQueries')}</InfoLabel>
          <InfoValue>{item.specificQueries}</InfoValue>
        </ModalInfoGrid>
      </ModalSection>

      <ModalSection>
        <SectionTitle>{t('pbl.pagesHardcoded.ocManageCases.caseInformationModal.enquiryRequirementsExpectations')}</SectionTitle>
        <ModalInfoGrid>
          <InfoLabel>{t('pbl.pagesHardcoded.ocManageCases.caseInformationModal.primaryLegalServiceNeeds')}</InfoLabel>
          <InfoValue>{translatePblDisplayList(t, item.primaryLegalServiceNeeds)}</InfoValue>
          <InfoLabel>{t('pbl.pagesHardcoded.ocManageCases.caseInformationModal.preferredConsultationMethodViaEbramDmp')}</InfoLabel>
          <InfoValue>{translatePblDisplayList(t, item.preferredConsultationMethods)}</InfoValue>
        </ModalInfoGrid>
      </ModalSection>

      <ModalSection>
        <SectionTitle>{t('pbl.pagesHardcoded.ocManageCases.caseInformationModal.documentSUpload')}</SectionTitle>
        {(item.supportingDocuments || []).length > 0 ? (
          (item.supportingDocuments || []).map((document) => (
            <ModalInfoGrid key={document.id}>
              <InfoLabel>{t('pbl.common.labels.title', { defaultValue: 'Title' })}</InfoLabel>
              <InfoValue>{document.title || '-'}</InfoValue>
              <InfoLabel>{t('pbl.pagesHardcoded.ocManageCases.caseInformationModal.documentType')}</InfoLabel>
              <InfoValue>{document.document_type || '-'}</InfoValue>
              <InfoLabel>{t('pbl.pagesHardcoded.ocManageCases.caseInformationModal.file')}</InfoLabel>
              <InfoValue>
                {document.storage_file?.path ? (
                  <SupportingDocumentLink href="#" onClick={(event) => { event.preventDefault(); void openStorageDownload(document.storage_file!.path); }}>
                    {document.storage_file.name || document.title || t('pbl.pagesHardcoded.ocManageCases.caseInformationModal.openFile')}
                  </SupportingDocumentLink>
                ) : (
                  document.storage_file?.name || '-'
                )}
              </InfoValue>
            </ModalInfoGrid>
          ))
        ) : (
          <InfoValue>{t('pbl.pagesHardcoded.ocManageCases.caseInformationModal.noDocumentsUploaded')}</InfoValue>
        )}
      </ModalSection>

      <ModalSection>
        <SectionTitle>{t('pbl.pagesHardcoded.ocManageCases.caseInformationModal.signature')}</SectionTitle>
        <Text margin={0}>
          <CheckIcon>&#10003;</CheckIcon>
          {t('pbl.pagesHardcoded.ocManageCases.caseInformationModal.signatureOfEnquirer')}<br />
          <InfoLabel as="span">{t('pbl.pagesHardcoded.ocManageCases.caseInformationModal.date')}{formatPblDisplayDate(item.enquirerSignatureDate)}</InfoLabel>
        </Text>
        {item.enquirerSignatureImage ? (
          <SignaturePreview>
            <SignaturePreviewImage src={convertLink(item.enquirerSignatureImage)} alt={t('pbl.pagesHardcoded.ocManageCases.caseInformationModal.signatureOfEnquirer')} />
          </SignaturePreview>
        ) : null}
      </ModalSection>
    </InfoModalSurface>
  </Modal>
);
};
